import React, { useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { unwrapResult } from '@reduxjs/toolkit';
import { useSelector, useDispatch } from 'react-redux';
import { getNotificationToken, subscribeToForegroundMessages } from '../services/firebase';
import { selectAuthInfo } from '../../features/auth/authSlice';
import {
  openPushNotification,
  subscribeForNotifications,
  getPushes
} from '../../features/user/userSlice';
import ToastMessage from '../components/pushNotifications/ToastMessage';

const NotificationProvider = ({ children }) => {
  const dispatch = useDispatch();
  const { isAuthorized } = useSelector(selectAuthInfo);
  const [firebaseToken, setFirebaseToken] = useState(null);

  const refetchPushes = useCallback(async () => {
    unwrapResult(await dispatch(getPushes()));
  }, [dispatch]);

  const connectDevice = useCallback(
    async (deviceID) => {
      unwrapResult(await dispatch(subscribeForNotifications({ subscription_token: deviceID })));
    },
    [dispatch]
  );

  useEffect(() => {
    getNotificationToken(setFirebaseToken);
  }, []);

  useEffect(() => {
    if (!isAuthorized) return;
    dispatch(getPushes());
  }, [dispatch, isAuthorized]);

  useEffect(() => {
    if (firebaseToken && isAuthorized) {
      connectDevice(firebaseToken);
    }
  }, [firebaseToken, isAuthorized, connectDevice]);

  const handlePushClick = useCallback(() => {
    if (isAuthorized) refetchPushes();
    dispatch(openPushNotification(true));
  }, [dispatch, isAuthorized, refetchPushes]);

  useEffect(() => {
    const unsubscribe = subscribeToForegroundMessages((payload) => {
      if (payload) {
        if (isAuthorized) refetchPushes();
        toast.info(
          <ToastMessage
            title={payload?.notification?.title || ''}
            body={payload?.notification?.body || ''}
            icon={payload?.notification?.image || ''}
          />,
          {
            autoClose: 10000,
            type: 'dark',
            onClick: handlePushClick,
            position: 'bottom-left',
            style: { background: '#404040' },
            progressStyle: { background: 'white' }
          }
        );
      }
    });

    return () => {
      unsubscribe();
    };
  }, [handlePushClick, isAuthorized, refetchPushes]);

  return <>{children}</>;
};

NotificationProvider.propTypes = {
  children: PropTypes.node
};

export default NotificationProvider;
